import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FaMapMarkerAlt,
  FaCamera,
  FaRulerCombined,
  FaExclamationTriangle,
  FaPaperPlane,
  FaArrowLeft,
  FaCrosshairs,
  FaCheckCircle,
  FaMap,
} from "react-icons/fa";
import { createPotholeReport } from "../../store/potholeSlice";
import MapLocationPicker from "../../components/MapLocationPicker";

const inputClass =
  "w-full rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 outline-none transition focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15";

const Report = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    latitude: "",
    longitude: "",
    address: "",
    description: "",
    severity: "medium",
    width: "",
    depth: "",
    image: null,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, image: file }));
    const reader = new FileReader();
    reader.onload = (event) => setImagePreview(event.target.result);
    reader.readAsDataURL(file);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser.");
      return;
    }

    setLocating(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData((prev) => ({
          ...prev,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }));
        toast.success("Location captured");
        setLocating(false);
      },
      () => {
        setError(
          "Unable to get your location. Please enter coordinates manually."
        );
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleMapConfirm = ({ latitude, longitude, addressHint }) => {
    setFormData((prev) => ({
      ...prev,
      latitude,
      longitude,
      address: prev.address?.trim() ? prev.address : addressHint || prev.address,
    }));
    setMapOpen(false);
    setError("");
    toast.success("Location selected from map");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!formData.latitude || !formData.longitude || !formData.address) {
      setError("Please provide latitude, longitude, and address");
      setLoading(false);
      return;
    }

    if (!formData.image) {
      setError("Pothole image is required");
      setLoading(false);
      return;
    }

    try {
      const submitData = new FormData();
      submitData.append("latitude", formData.latitude);
      submitData.append("longitude", formData.longitude);
      submitData.append("address", formData.address);
      submitData.append("description", formData.description);
      submitData.append("severity", formData.severity);

      if (formData.width && formData.depth) {
        submitData.append(
          "size",
          JSON.stringify({
            width: parseInt(formData.width, 10),
            depth: parseInt(formData.depth, 10),
          })
        );
      }

      submitData.append("image", formData.image);

      const created = await dispatch(createPotholeReport(submitData));
      setSuccess("Pothole reported successfully!");
      toast.success("Pothole reported successfully!");
      setFormData({
        latitude: "",
        longitude: "",
        address: "",
        description: "",
        severity: "medium",
        width: "",
        depth: "",
        image: null,
      });
      setImagePreview(null);

      setTimeout(() => {
        navigate(created?._id ? `/pothole/${created._id}` : "/");
      }, 1200);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Error connecting to the server";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const severityOptions = [
    {
      value: "low",
      label: "Low",
      hint: "Small surface damage",
      active: "border-emerald-500 bg-emerald-50 text-emerald-800",
    },
    {
      value: "medium",
      label: "Medium",
      hint: "Noticeable hazard",
      active: "border-amber-500 bg-amber-50 text-amber-800",
    },
    {
      value: "high",
      label: "High",
      hint: "Urgent risk",
      active: "border-red-500 bg-red-50 text-red-800",
    },
  ];

  return (
    <div className="min-h-screen pb-16">
      <MapLocationPicker
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        onConfirm={handleMapConfirm}
        initialLat={formData.latitude}
        initialLng={formData.longitude}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 px-4 py-12 text-white sm:py-14">
        <div className="pointer-events-none absolute -right-8 top-0 h-48 w-48 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="relative mx-auto max-w-3xl">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-5 inline-flex items-center gap-2 text-sm text-white/75 transition hover:text-amber-300"
          >
            <FaArrowLeft /> Back
          </button>
          <p className="mb-2 text-sm font-semibold uppercase tracking-[0.2em] text-amber-400">
            Submit a hazard
          </p>
          <h1 className="font-display text-3xl font-bold sm:text-5xl">
            Report a pothole
          </h1>
          <p className="mt-3 max-w-xl text-white/75">
            Pin the location, add a clear photo, and help your community push
            for a faster fix.
          </p>
        </div>
      </section>

      <div className="relative z-10 mx-auto max-w-6xl px-4 pb-4 pt-6 sm:pt-8">
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-3xl border border-navy-900/10 bg-white p-5 shadow-[0_20px_50px_rgba(11,31,68,0.1)] sm:p-8">
            {success && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
                <FaCheckCircle className="mt-0.5 shrink-0" />
                <p>{success}</p>
              </div>
            )}
            {error && (
              <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              <section>
                <div className="mb-4 flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy-900/5 text-navy-800">
                    <FaMapMarkerAlt />
                  </span>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-navy-900">
                      Location
                    </h2>
                    <p className="text-sm text-navy-800/60">
                      Use GPS or enter coordinates manually
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-navy-900">
                      Latitude *
                    </label>
                    <input
                      type="number"
                      step="any"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="27.7172"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-navy-900">
                      Longitude *
                    </label>
                    <input
                      type="number"
                      step="any"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="85.3240"
                      required
                    />
                  </div>
                </div>

                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={getCurrentLocation}
                    disabled={locating}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 text-sm font-semibold text-navy-900 transition hover:border-amber-500 hover:text-amber-600 disabled:opacity-60"
                  >
                    <FaCrosshairs className={locating ? "animate-spin" : ""} />
                    {locating ? "Getting location..." : "Use current location"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMapOpen(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-navy-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-800"
                  >
                    <FaMap />
                    Choose from map
                  </button>
                </div>

                <div className="mt-4">
                  <label className="mb-1.5 block text-sm font-medium text-navy-900">
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Street, landmark, or area name"
                    required
                  />
                </div>
              </section>

              <section>
                <h2 className="mb-3 font-display text-lg font-semibold text-navy-900">
                  Description
                </h2>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                  className={inputClass}
                  placeholder="What makes this hazard risky? Nearby landmarks?"
                />
              </section>

              <section>
                <div className="mb-3 flex items-center gap-2">
                  <FaExclamationTriangle className="text-amber-600" />
                  <h2 className="font-display text-lg font-semibold text-navy-900">
                    Severity
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {severityOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`cursor-pointer rounded-2xl border px-4 py-3 transition ${
                        formData.severity === option.value
                          ? option.active
                          : "border-navy-900/10 hover:border-navy-700/30"
                      }`}
                    >
                      <input
                        type="radio"
                        name="severity"
                        value={option.value}
                        checked={formData.severity === option.value}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <span className="block font-semibold">{option.label}</span>
                      <span className="mt-0.5 block text-xs opacity-70">
                        {option.hint}
                      </span>
                    </label>
                  ))}
                </div>
              </section>

              <section>
                <div className="mb-3 flex items-center gap-2">
                  <FaRulerCombined className="text-amber-600" />
                  <h2 className="font-display text-lg font-semibold text-navy-900">
                    Size <span className="text-sm font-normal text-navy-800/50">(optional)</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-navy-900">
                      Width (cm)
                    </label>
                    <input
                      type="number"
                      name="width"
                      value={formData.width}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g. 60"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-navy-900">
                      Depth (cm)
                    </label>
                    <input
                      type="number"
                      name="depth"
                      value={formData.depth}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="e.g. 12"
                      min="1"
                    />
                  </div>
                </div>
              </section>

              <section>
                <div className="mb-3 flex items-center gap-2">
                  <FaCamera className="text-navy-800" />
                  <h2 className="font-display text-lg font-semibold text-navy-900">
                    Photo *
                  </h2>
                </div>

                <div className="rounded-2xl border border-dashed border-navy-900/20 bg-surface/70 p-5 text-center sm:p-6">
                  <input
                    type="file"
                    id="image"
                    name="image"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                  />

                  {imagePreview ? (
                    <div className="mb-4">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="mx-auto max-h-56 rounded-xl object-cover shadow-md"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(null);
                          setFormData((prev) => ({ ...prev, image: null }));
                        }}
                        className="mt-3 text-sm font-medium text-red-600 hover:text-red-700"
                      >
                        Remove image
                      </button>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-navy-700/50 shadow-sm">
                        <FaCamera className="text-2xl" />
                      </div>
                      <p className="text-navy-800/70">
                        Upload a clear photo of the pothole
                      </p>
                    </div>
                  )}

                  <label
                    htmlFor="image"
                    className="inline-flex cursor-pointer rounded-xl bg-navy-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-800"
                  >
                    {imagePreview ? "Change image" : "Choose image"}
                  </label>
                </div>
              </section>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3.5 font-semibold text-navy-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <span className="h-5 w-5 animate-spin rounded-full border-b-2 border-navy-950" />
                ) : (
                  <>
                    <FaPaperPlane />
                    Submit report
                  </>
                )}
              </button>
            </form>
          </div>

          <aside className="space-y-4 lg:pt-2">
            <div className="rounded-3xl bg-gradient-to-b from-navy-900 to-navy-800 p-6 text-white shadow-lg">
              <h3 className="font-display text-xl font-semibold">
                Tips for a strong report
              </h3>
              <ul className="mt-4 space-y-3 text-sm text-white/75">
                <li className="rounded-xl bg-white/5 p-3">
                  Stand safely and capture the full hole plus nearby landmark.
                </li>
                <li className="rounded-xl bg-white/5 p-3">
                  Use GPS when possible for accurate map placement.
                </li>
                <li className="rounded-xl bg-white/5 p-3">
                  Mark severity honestly so urgent spots get priority.
                </li>
              </ul>
            </div>

            <div className="rounded-3xl border border-navy-900/10 bg-white p-6 shadow-sm">
              <h3 className="font-display text-lg font-semibold text-navy-900">
                What happens next?
              </h3>
              <ol className="mt-4 space-y-3 text-sm text-navy-800/70">
                <li>1. Your report appears on the community list.</li>
                <li>2. Neighbors can vote and comment.</li>
                <li>3. Status updates as it moves toward a fix.</li>
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Report;
